import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { fadeUp, fadeIn, viewport } from "@/lib/motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReflectionPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([
      axios.get(`${API}/reflections/${slug}`),
      axios.get(`${API}/reflections`),
    ])
      .then(([one, all]) => {
        setItem(one.data);
        setRelated(all.data.filter((r) => r.slug !== slug));
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-bone min-h-screen">
        <Nav />
        <div className="py-40 text-center text-stone italic font-serif">Loading…</div>
      </div>
    );
  }
  if (notFound || !item) {
    return (
      <div className="bg-bone min-h-screen">
        <Nav />
        <div className="py-40 text-center">
          <p className="font-serif italic text-3xl text-charcoal">Reflection not found.</p>
          <Link to="/" className="mt-6 inline-block text-bronze link-underline text-sm uppercase tracking-[0.22em]">
            Return home
          </Link>
        </div>
      </div>
    );
  }

  const paragraphs = item.body.split("\n\n");

  return (
    <div className="bg-bone min-h-screen text-charcoal">
      <Nav />

      {/* Hero / cover */}
      <header className="relative pt-32 md:pt-40 pb-16 md:pb-24" data-testid="reflection-detail">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link
            to="/#reflections"
            data-testid="reflection-back"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-stone hover:text-bronze transition-colors duration-500"
          >
            <ArrowLeft size={14} /> Back to reflections
          </Link>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-12 text-[10px] uppercase tracking-[0.3em] text-bronze"
          >
            Essay · {item.reading_time}
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            data-testid="reflection-title"
            className="mt-4 font-serif text-6xl md:text-8xl text-charcoal leading-[0.95] tracking-tight"
          >
            {item.title}
          </motion.h1>
          {item.subtitle && (
            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="mt-4 font-serif italic text-2xl text-forest"
            >
              {item.subtitle}
            </motion.p>
          )}
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-10 text-[10px] uppercase tracking-[0.3em] text-stone"
          >
            — Written by Hadil El-Baba
          </motion.p>
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="mt-16 max-w-6xl mx-auto px-6 md:px-12"
        >
          <div className="aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-sm">
            <img src={item.cover_image} alt={item.title} className="w-full h-full object-cover" />
          </div>
        </motion.div>
      </header>

      {/* Body */}
      <article className="max-w-2xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {paragraphs.map((p, i) => {
          const isOpening = i === 0;
          return (
            <motion.p
              key={i}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              variants={fadeUp}
              data-testid={`reflection-paragraph-${i}`}
              className={`mb-6 text-ink leading-[1.85] font-light ${
                isOpening
                  ? "font-serif italic text-2xl md:text-3xl text-forest leading-snug"
                  : "text-lg"
              }`}
            >
              {p}
            </motion.p>
          );
        })}

        <div className="mt-16 pt-10 border-t border-stone/30 text-center">
          <p className="font-serif italic text-2xl text-forest">— Hadil</p>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-sand py-20 md:py-28">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <p className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-4">
              ✦ &nbsp; Continue reading
            </p>
            <h3 className="font-serif text-4xl md:text-5xl text-charcoal leading-tight mb-12">
              Other reflections
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/reflections/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="group grid grid-cols-3 gap-5 items-center"
                >
                  <div className="zoom-container col-span-1 aspect-square overflow-hidden rounded-sm">
                    <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="col-span-2">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-stone">{r.reading_time}</p>
                    <h4 className="mt-2 font-serif text-3xl text-charcoal group-hover:text-bronze transition-colors duration-500">
                      {r.title}
                    </h4>
                    <p className="mt-2 text-sm text-ink/80 leading-relaxed line-clamp-2">{r.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
